import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES, UNEVO } from "@/data-unevo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export default function UnevoContact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setForm(empty);
      toast.success("Message sent — we'll reply within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="unevo-contact"
      className="relative overflow-hidden bg-[#0B0F1A] py-24 text-white md:py-32"
    >
      <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-vibrant-orange/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-emerald-green/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
            Get in Touch
          </span>
          <h2 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Let&apos;s build
            <span className="block text-vibrant-orange">something powerful.</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">
            Tell us about your project, hiring need, or growth goal. Our team gets back within one business day, no obligation.
          </p>

          <div className="mt-10 space-y-5 text-sm text-white/70">
            {UNEVO.emails.map((e) => (
              <a
                key={e}
                href={`mailto:${e}`}
                className="inline-flex items-center gap-3 transition-colors hover:text-emerald-green"
              >
                <Mail size={18} className="text-emerald-green" />
                {e}
              </a>
            ))}
            <div className="flex items-start gap-3">
              <MapPin size={18} className="mt-0.5 shrink-0 text-emerald-green" />
              <span>{UNEVO.headOffice}</span>
            </div>
            <a
              href={`https://wa.me/${UNEVO.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 transition-colors hover:text-emerald-green"
            >
              <Phone size={18} className="text-emerald-green" /> WhatsApp: +91 8603834988
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="unevo-contact-success"
              className="flex flex-col items-start gap-4 rounded-3xl border border-emerald-green/30 bg-emerald-green/5 p-10"
            >
              <CheckCircle2 size={44} className="text-emerald-green" />
              <h3 className="font-heading text-2xl font-bold">Message received.</h3>
              <p className="max-w-md text-sm text-white/70">
                Thanks — a strategist will email you within one business day with next steps.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-2 rounded-full border border-white/20 px-5 py-2 text-sm font-medium text-white/80 hover:bg-white/5"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="unevo-contact-form"
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-sm md:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name" className="text-white/70">Full Name *</Label>
                  <Input
                    id="c-name"
                    data-testid="unevo-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="e.g. Rohan Sharma"
                    className="border-white/15 bg-white/5 text-white placeholder:text-white/30"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-email" className="text-white/70">Email *</Label>
                  <Input
                    id="c-email"
                    type="email"
                    data-testid="unevo-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@company.com"
                    className="border-white/15 bg-white/5 text-white placeholder:text-white/30"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-phone" className="text-white/70">Phone</Label>
                  <Input
                    id="c-phone"
                    data-testid="unevo-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91 ..."
                    className="border-white/15 bg-white/5 text-white placeholder:text-white/30"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-company" className="text-white/70">Company</Label>
                  <Input
                    id="c-company"
                    data-testid="unevo-company"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Company name"
                    className="border-white/15 bg-white/5 text-white placeholder:text-white/30"
                  />
                </div>
              </div>

              <div className="mt-5 space-y-1.5">
                <Label className="text-white/70">Interested in</Label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="unevo-service" className="border-white/15 bg-white/5 text-white">
                    <SelectValue placeholder="Pick a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-5 space-y-1.5">
                <Label htmlFor="c-msg" className="text-white/70">Tell us about your project</Label>
                <Textarea
                  id="c-msg"
                  data-testid="unevo-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Goals, timeline, budget range..."
                  rows={4}
                  className="border-white/15 bg-white/5 text-white placeholder:text-white/30"
                />
              </div>

              <button
                type="submit"
                data-testid="unevo-contact-submit"
                disabled={loading}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-vibrant-orange px-6 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:bg-orange-600 disabled:opacity-60"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
